import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Palette, 
  Smartphone, 
  Monitor, 
  Tablet, 
  Send, 
  Save, 
  RotateCcw, 
  Copy, 
  Check, 
  Eye, 
  Code, 
  Sparkles, 
  Mail, 
  ExternalLink,
  ShieldCheck,
  FileText,
  Sliders,
  CheckCircle2,
  RefreshCw,
  Phone,
  MapPin,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { 
  EmailTemplateConfig, 
  DEFAULT_EMAIL_CONFIG,
  generateAdminServiceQuoteEmail,
  generateClientServiceQuoteConfirmation,
  generateAdminWebsiteOnboardingEmail,
  generateClientWebsiteOnboardingConfirmation,
  generateAdminOtpEmail,
  SAMPLE_SERVICE_QUOTE,
  SAMPLE_WEBSITE_ONBOARDING
} from '../lib/emailTemplates';
import { fetchEmailTemplateSettings, saveEmailTemplateSettings } from '../lib/dbService';

export interface EmailTemplateCustomizerProps {
  adminEmail: string;
}

type TemplateType = 
  | 'service_quote_admin'
  | 'service_quote_client'
  | 'website_onboarding_admin'
  | 'website_onboarding_client'
  | 'admin_2fa_otp';

type ViewportMode = 'mobile' | 'tablet' | 'desktop';
type ViewMode = 'visual' | 'code';

const COLOR_PRESETS = [
  { name: 'Amber Gold', primary: '#f59e0b', end: '#d97706' },
  { name: 'Emerald', primary: '#10b981', end: '#059669' },
  { name: 'Sky Blue', primary: '#38bdf8', end: '#0284c7' },
  { name: 'Amethyst', primary: '#a855f7', end: '#7e22ce' },
  { name: 'Crimson', primary: '#ef4444', end: '#b91c1c' },
  { name: 'Pure Platinum', primary: '#e2e8f0', end: '#94a3b8' },
];

export const EmailTemplateCustomizer: React.FC<EmailTemplateCustomizerProps> = ({ adminEmail }) => {
  const [config, setConfig] = useState<EmailTemplateConfig>(DEFAULT_EMAIL_CONFIG);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('service_quote_client');
  const [viewport, setViewport] = useState<ViewportMode>('desktop');
  const [viewMode, setViewMode] = useState<ViewMode>('visual');
  const [activeSection, setActiveSection] = useState<'branding' | 'contact' | 'content'>('branding');
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  // Load saved settings from Firestore / LocalStorage on mount
  useEffect(() => {
    let isMounted = true;
    async function loadSettings() {
      try {
        const saved = await fetchEmailTemplateSettings();
        if (isMounted) {
          setConfig(saved);
        }
      } catch (err) {
        console.error('Error loading email settings:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }
    loadSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotice({ message, type });
    setTimeout(() => {
      setNotice(null);
    }, 4500);
  };

  // Generate current template payload dynamically
  const currentRender = useMemo(() => {
    const dummyRef = 'RB-DEMO88';
    switch (selectedTemplate) {
      case 'service_quote_admin':
        return generateAdminServiceQuoteEmail(SAMPLE_SERVICE_QUOTE, dummyRef, config);
      case 'service_quote_client':
        return generateClientServiceQuoteConfirmation(SAMPLE_SERVICE_QUOTE, dummyRef, config);
      case 'website_onboarding_admin':
        return generateAdminWebsiteOnboardingEmail(SAMPLE_WEBSITE_ONBOARDING, dummyRef, config);
      case 'website_onboarding_client':
        return generateClientWebsiteOnboardingConfirmation(SAMPLE_WEBSITE_ONBOARDING, dummyRef, config);
      case 'admin_2fa_otp':
        return generateAdminOtpEmail(adminEmail || 'randon.brown.it@gmail.com', '573889', config);
      default:
        return generateClientServiceQuoteConfirmation(SAMPLE_SERVICE_QUOTE, dummyRef, config);
    }
  }, [selectedTemplate, config, adminEmail]);

  // Handle configuration changes
  const updateConfig = (key: keyof EmailTemplateConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Save changes to cloud
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const ok = await saveEmailTemplateSettings(config);
      if (ok) {
        showNotification('Email template layout and branding saved successfully to cloud!');
      } else {
        showNotification('Saved to local session, but cloud database write was restricted.', 'error');
      }
    } catch (err) {
      showNotification('Failed to save settings. Please check your network.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to factory defaults
  const handleReset = () => {
    if (window.confirm('Reset all email layout customizations back to default Obsidian & Gold?')) {
      setConfig({ ...DEFAULT_EMAIL_CONFIG });
      showNotification('Templates reset to default branding settings.');
    }
  };

  // Send real test email to current admin
  const handleSendTestEmail = async () => {
    const targetEmail = adminEmail || 'randon.brown.it@gmail.com';
    setIsSendingTest(true);
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: [targetEmail],
          subject: `[TEST PREVIEW] ${currentRender.subject}`,
          text: currentRender.text,
          html: currentRender.html,
          type: 'admin_alert'
        })
      });

      const data = (await res.json()) as { success?: boolean; error?: string };
      if (res.ok && (data.success !== false)) {
        showNotification(`Test email dispatched successfully to ${targetEmail}! Check your inbox.`);
      } else {
        showNotification(`Failed to dispatch test: ${data.error || 'Server error'}`, 'error');
      }
    } catch (err) {
      showNotification('Network error while dispatching test email.', 'error');
    } finally {
      setIsSendingTest(false);
    }
  };

  // Copy raw HTML code to clipboard
  const handleCopyHtml = () => {
    navigator.clipboard.writeText(currentRender.html);
    setCopiedCode(true);
    showNotification('Raw email HTML copied to clipboard!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Viewport width styling for preview container
  const viewportWidthClass = {
    mobile: 'w-[375px] max-w-full',
    tablet: 'w-[480px] max-w-full',
    desktop: 'w-[640px] max-w-full'
  }[viewport];

  return (
    <div className="space-y-6 text-left">
      {/* Toast Notification */}
      <AnimatePresence>
        {notice && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 text-xs font-semibold ${
              notice.type === 'success' 
                ? 'bg-emerald-950/80 border-emerald-500/40 text-emerald-200' 
                : 'bg-red-950/80 border-red-500/40 text-red-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>{notice.message}</span>
            </div>
            <button 
              onClick={() => setNotice(null)} 
              className="text-slate-400 hover:text-white px-2 py-0.5 rounded cursor-pointer"
            >
              &times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Template Selector Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl bg-[#09090b] border border-neutral-800">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl border border-brand-500/20">
            <Palette className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white font-display">Email Template Customizer & Studio</h3>
            <p className="text-[11px] font-mono text-slate-400">Preview live HTML, adjust branding, and test deliverability</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Template Select Dropdown */}
          <div className="relative">
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value as TemplateType)}
              className="bg-neutral-900 border border-neutral-700 text-white text-xs font-medium rounded-xl px-3 py-2 pr-8 focus:outline-none focus:border-brand-500 cursor-pointer appearance-none"
            >
              <option value="service_quote_client">1. Service Quote (Client Confirmation Receipt)</option>
              <option value="service_quote_admin">2. Service Quote (Admin Alert Notification)</option>
              <option value="website_onboarding_client">3. Website Onboarding (Client Brief Receipt)</option>
              <option value="website_onboarding_admin">4. Website Onboarding (Admin Alert Brief)</option>
              <option value="admin_2fa_otp">5. Administrator 2FA Security Passcode</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Action Buttons */}
          <button
            onClick={handleSendTestEmail}
            disabled={isSendingTest}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-200 border border-neutral-700 text-xs font-semibold cursor-pointer transition-all active:scale-95 disabled:opacity-50"
            title="Dispatch this preview directly to your inbox via Resend"
          >
            {isSendingTest ? <RefreshCw className="w-3.5 h-3.5 text-brand-400 animate-spin" /> : <Send className="w-3.5 h-3.5 text-brand-400" />}
            <span>Send Test Email</span>
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-brand-500 to-amber-600 hover:from-brand-400 hover:to-amber-500 text-neutral-950 text-xs font-bold shadow-md cursor-pointer transition-all active:scale-95 disabled:opacity-50"
          >
            {isSaving ? <RefreshCw className="w-3.5 h-3.5 text-black animate-spin" /> : <Save className="w-3.5 h-3.5 text-black" />}
            <span>Save Settings</span>
          </button>

          <button
            onClick={handleReset}
            className="p-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-400 hover:text-white border border-neutral-800 transition-all cursor-pointer"
            title="Reset to default branding"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Split Grid: Left Sidebar Controls + Right Live Canvas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* ===================== LEFT: CONTROLS PANEL (5 cols) ===================== */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Control Section Tabs */}
          <div className="flex rounded-xl bg-[#09090b] p-1 border border-neutral-800 text-xs font-semibold">
            <button
              onClick={() => setActiveSection('branding')}
              className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeSection === 'branding' 
                  ? 'bg-neutral-800 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Palette className="w-3.5 h-3.5 text-brand-400" />
              <span>Branding</span>
            </button>
            <button
              onClick={() => setActiveSection('contact')}
              className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeSection === 'contact' 
                  ? 'bg-neutral-800 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Phone className="w-3.5 h-3.5 text-brand-400" />
              <span>Contact</span>
            </button>
            <button
              onClick={() => setActiveSection('content')}
              className={`flex-1 py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeSection === 'content' 
                  ? 'bg-neutral-800 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sliders className="w-3.5 h-3.5 text-brand-400" />
              <span>Layout</span>
            </button>
          </div>

          {/* Section 1: Branding & Visuals */}
          {activeSection === 'branding' && (
            <motion.div 
              initial={{ opacity: 0, y: 4 }} 
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-2xl bg-[#09090b] border border-neutral-800 space-y-4 text-xs"
            >
              <div className="border-b border-neutral-800 pb-2">
                <span className="font-bold text-white uppercase tracking-wider text-[11px] font-mono">
                  Visual Identity & Theme
                </span>
              </div>

              {/* Brand Mark Initials & Brand Name */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1 font-mono text-[10px]">Brand Mark</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={config.brandMark}
                    onChange={(e) => updateConfig('brandMark', e.target.value)}
                    className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white font-mono text-center font-bold focus:border-brand-500 focus:outline-none"
                    placeholder="RB"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-slate-400 mb-1 font-mono text-[10px]">Company / Brand Name</label>
                  <input
                    type="text"
                    value={config.brandName}
                    onChange={(e) => updateConfig('brandName', e.target.value)}
                    className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white font-medium focus:border-brand-500 focus:outline-none"
                    placeholder="Randon Brown"
                  />
                </div>
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Header Subtitle</label>
                <input
                  type="text"
                  value={config.brandSubtitle}
                  onChange={(e) => updateConfig('brandSubtitle', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="IT Leader • Technology • Websites"
                />
              </div>

              {/* Color Preset Palette */}
              <div>
                <label className="block text-slate-400 mb-1.5 font-mono text-[10px]">Primary Accent Palette</label>
                <div className="grid grid-cols-3 gap-2">
                  {COLOR_PRESETS.map((preset) => {
                    const isSelected = config.accentColor === preset.primary;
                    return (
                      <button
                        key={preset.name}
                        onClick={() => {
                          updateConfig('accentColor', preset.primary);
                          updateConfig('accentGradientEnd', preset.end);
                        }}
                        className={`p-2 rounded-xl border flex items-center gap-2 cursor-pointer transition-all text-left ${
                          isSelected 
                            ? 'bg-neutral-800 border-white text-white' 
                            : 'bg-neutral-900/60 border-neutral-800 text-slate-400 hover:text-white hover:border-neutral-700'
                        }`}
                      >
                        <span 
                          className="w-3.5 h-3.5 rounded-full shrink-0 shadow-sm"
                          style={{ backgroundColor: preset.primary }}
                        />
                        <span className="text-[10px] font-medium truncate">{preset.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Hex Inputs */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-slate-400 mb-1 font-mono text-[10px]">Custom Accent Hex</label>
                  <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-700 rounded-lg p-1.5">
                    <input
                      type="color"
                      value={config.accentColor}
                      onChange={(e) => updateConfig('accentColor', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                    />
                    <input
                      type="text"
                      value={config.accentColor}
                      onChange={(e) => updateConfig('accentColor', e.target.value)}
                      className="bg-transparent text-white font-mono text-xs w-full focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-400 mb-1 font-mono text-[10px]">Gradient End Hex</label>
                  <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-700 rounded-lg p-1.5">
                    <input
                      type="color"
                      value={config.accentGradientEnd}
                      onChange={(e) => updateConfig('accentGradientEnd', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                    />
                    <input
                      type="text"
                      value={config.accentGradientEnd}
                      onChange={(e) => updateConfig('accentGradientEnd', e.target.value)}
                      className="bg-transparent text-white font-mono text-xs w-full focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Section 2: Contact Information */}
          {activeSection === 'contact' && (
            <motion.div 
              initial={{ opacity: 0, y: 4 }} 
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-2xl bg-[#09090b] border border-neutral-800 space-y-4 text-xs"
            >
              <div className="border-b border-neutral-800 pb-2">
                <span className="font-bold text-white uppercase tracking-wider text-[11px] font-mono">
                  Sender Contact & Footer Details
                </span>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Support / Sender Email</label>
                <input
                  type="email"
                  value={config.supportEmail}
                  onChange={(e) => updateConfig('supportEmail', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white font-mono text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="help@randonbrown.com"
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Support Telephone</label>
                <input
                  type="text"
                  value={config.supportPhone}
                  onChange={(e) => updateConfig('supportPhone', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="(314) 944-3074"
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Geographic Location Line</label>
                <input
                  type="text"
                  value={config.locationText}
                  onChange={(e) => updateConfig('locationText', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="Texas County • Licking, Missouri, United States"
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Custom Footer Note (Optional)</label>
                <textarea
                  rows={2}
                  value={config.customFooterNote || ''}
                  onChange={(e) => updateConfig('customFooterNote', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="e.g. Estimates valid for 30 days. Fast turnaround guaranteed."
                />
              </div>
            </motion.div>
          )}

          {/* Section 3: Content & Layout Features */}
          {activeSection === 'content' && (
            <motion.div 
              initial={{ opacity: 0, y: 4 }} 
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-2xl bg-[#09090b] border border-neutral-800 space-y-4 text-xs"
            >
              <div className="border-b border-neutral-800 pb-2">
                <span className="font-bold text-white uppercase tracking-wider text-[11px] font-mono">
                  Message Structure & Component Toggles
                </span>
              </div>

              {/* Digital Signature Card Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-neutral-900 border border-neutral-800">
                <div>
                  <span className="text-white font-medium block">Show Digital Signature Card</span>
                  <span className="text-[10px] text-slate-400">Displays client digital signature, timestamp, and verification pill</span>
                </div>
                <button
                  type="button"
                  onClick={() => updateConfig('showSignatureCard', !config.showSignatureCard)}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    config.showSignatureCard ? 'bg-brand-500' : 'bg-neutral-700'
                  }`}
                >
                  <span 
                    className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${
                      config.showSignatureCard ? 'translate-x-5' : 'translate-x-0'
                    }`} 
                  />
                </button>
              </div>

              {/* CTA Button Text */}
              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Quote Request Call-to-Action Text</label>
                <input
                  type="text"
                  value={config.ctaButtonTextQuote || ''}
                  onChange={(e) => updateConfig('ctaButtonTextQuote', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="Reply Directly to Client"
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Onboarding Call-to-Action Text</label>
                <input
                  type="text"
                  value={config.ctaButtonTextOnboarding || ''}
                  onChange={(e) => updateConfig('ctaButtonTextOnboarding', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="Reply Directly to Client"
                />
              </div>

              {/* Custom Greetings */}
              <div>
                <label className="block text-slate-400 mb-1 font-mono text-[10px]">Client Quote Confirmation Greeting Text</label>
                <textarea
                  rows={2}
                  value={config.customGreetingQuote || ''}
                  onChange={(e) => updateConfig('customGreetingQuote', e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-lg p-2 text-white text-xs focus:border-brand-500 focus:outline-none"
                  placeholder="Thank you for reaching out! We have safely received your inquiry."
                />
              </div>
            </motion.div>
          )}

          {/* Quick Info Box */}
          <div className="p-4 rounded-xl bg-neutral-900/50 border border-neutral-800 text-[11px] text-slate-400 space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-slate-300">
              <Sparkles className="w-3.5 h-3.5 text-brand-400" />
              <span>Production Synchronization</span>
            </div>
            <p className="leading-relaxed">
              When you click <strong>Save Settings</strong>, your customizations are saved to Firestore (<code className="text-brand-300">settings/email_templates</code>) and cached locally. Every incoming quote and onboarding intake automatically renders using your custom branding.
            </p>
          </div>
        </div>

        {/* ===================== RIGHT: LIVE INTERACTIVE PREVIEW CANVAS (7 cols) ===================== */}
        <div className="lg:col-span-7 space-y-3">
          
          {/* Canvas Toolbar: Viewport & View Mode Switches */}
          <div className="flex items-center justify-between p-2.5 rounded-xl bg-[#09090b] border border-neutral-800">
            {/* Viewport Selectors */}
            <div className="flex items-center gap-1 bg-neutral-900 p-1 rounded-lg">
              <button
                onClick={() => setViewport('desktop')}
                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                  viewport === 'desktop' ? 'bg-neutral-800 text-brand-400 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="Desktop View (640px)"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewport('tablet')}
                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                  viewport === 'tablet' ? 'bg-neutral-800 text-brand-400 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="Tablet View (480px)"
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewport('mobile')}
                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                  viewport === 'mobile' ? 'bg-neutral-800 text-brand-400 shadow' : 'text-slate-400 hover:text-white'
                }`}
                title="Mobile View (375px)"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>

            {/* Subject Preview Pill */}
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-300 truncate max-w-xs font-mono">
              <Mail className="w-3.5 h-3.5 text-brand-400 shrink-0" />
              <span className="truncate">{currentRender.subject}</span>
            </div>

            {/* Visual vs Code Mode */}
            <div className="flex items-center gap-1 bg-neutral-900 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('visual')}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                  viewMode === 'visual' ? 'bg-neutral-800 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Visual</span>
              </button>
              <button
                onClick={() => setViewMode('code')}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                  viewMode === 'code' ? 'bg-neutral-800 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                <Code className="w-3.5 h-3.5" />
                <span>HTML</span>
              </button>
              {viewMode === 'code' && (
                <button
                  onClick={handleCopyHtml}
                  className="p-1 rounded text-slate-400 hover:text-brand-400 transition-colors ml-1 cursor-pointer"
                  title="Copy Raw HTML"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          </div>

          {/* Canvas Wrapper */}
          <div className="relative rounded-2xl bg-[#060608] border border-neutral-800/80 p-4 md:p-6 flex justify-center items-start min-h-[620px] max-h-[750px] overflow-y-auto custom-scrollbar">
            {viewMode === 'visual' ? (
              <div className={`${viewportWidthClass} transition-all duration-300 shadow-2xl rounded-2xl overflow-hidden border border-neutral-800`}>
                <iframe
                  title="Email Template Live Preview"
                  srcDoc={currentRender.html}
                  className="w-full h-[680px] bg-neutral-950 border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <div className="w-full h-[650px] bg-[#0c0c0c] border border-neutral-800 rounded-xl p-4 overflow-auto custom-scrollbar font-mono text-[11px] text-slate-300 leading-relaxed select-all">
                <pre>{currentRender.html}</pre>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
